import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  Tabs,
  Text,
  useToast,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import {
  deleteSourcesUsesScenarioApi,
  freezeSourcesUsesScenarioApi,
  getSourcesUsesScenarioApi,
  listSourcesUsesScenariosApi,
} from 'api/dealApi';

import CashFlowFeeExposurePanel from './CashFlowFeeExposure';
import SourcesUsesScenarioList, { scenarioWindowText } from './CashFlowFeeExposure/SourcesUsesScenarios';

const TENANT = 'Treisar';

const TABS = [
  { screen: 'configuration', label: 'Configuration' },
  { screen: 'marketing', label: 'Marketing & Funding' },
  { screen: 'cashflow', label: 'Cashflow' },
  { screen: 'su', label: 'S&U' },
  { screen: 'fee', label: 'Fee' },
  { screen: 'facility', label: 'Facility fee' },
  { screen: 'company-cash', label: 'Company cash' },
];
const SCREENS = TABS.map((tab) => tab.screen);

const LIST = { kind: 'list' };

// view.kind: 'list' (first page), 'main', 'edit' (new or unfinished scenario: id is
// empty until the first Save & Run), 'view' (finished scenario, read-only).
const SourcesAndUses = () => {
  const toast = useToast();
  const panelRef = useRef(null);
  const [view, setView] = useState(LIST);
  const [screen, setScreen] = useState(SCREENS[0]);
  const [scenarios, setScenarios] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [openScenario, setOpenScenario] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [savingToLeave, setSavingToLeave] = useState(false);

  const error = useCallback((description) => toast({ title: 'Error', description, status: 'error' }), [toast]);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const list = await listSourcesUsesScenariosApi({ tenant: TENANT });
      setScenarios(Array.isArray(list) ? list : []);
    } catch {
      setScenarios([]);
      error('Failed to load the scenarios');
    } finally {
      setListLoading(false);
    }
  }, [error]);

  useEffect(() => {
    if (view.kind === 'list') loadList();
  }, [view.kind, loadList]);

  // Closing or refreshing the browser with unsaved changes shows the browser's own warning.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const enter = (next, firstScreen) => {
    setDirty(false);
    setScreen(firstScreen);
    setView(next);
  };

  const openMain = () => enter({ kind: 'main' }, 'configuration');
  const startNew = (name) => enter({ kind: 'edit', id: '', name }, 'configuration');

  const openFromList = async (scenario) => {
    if (!scenario.frozen) {
      enter({ kind: 'edit', id: scenario.id, name: scenario.name }, 'configuration');
      return;
    }
    setBusyId(scenario.id);
    try {
      const full = await getSourcesUsesScenarioApi({ tenant: TENANT, scenarioId: scenario.id });
      setOpenScenario(full);
      enter({ kind: 'view', id: scenario.id, name: scenario.name }, 'marketing');
    } catch {
      error('Failed to open the scenario');
    } finally {
      setBusyId('');
    }
  };

  const finish = async (scenario) => {
    setBusyId(scenario.id);
    try {
      await freezeSourcesUsesScenarioApi({ tenant: TENANT, scenarioId: scenario.id });
      await loadList();
    } catch {
      error('Failed to finish the scenario');
    } finally {
      setBusyId('');
    }
  };

  const remove = async (scenario) => {
    setBusyId(scenario.id);
    try {
      await deleteSourcesUsesScenarioApi({ tenant: TENANT, scenarioId: scenario.id });
      toast({ title: `Deleted ${scenario.name}`, status: 'success', duration: 3000 });
      await loadList();
    } catch {
      error('Failed to delete the scenario');
    } finally {
      setBusyId('');
    }
  };

  // Leaving a scenario finishes it. A scenario that was never run has nothing to finish.
  const leave = async () => {
    setLeaving(false);
    if (view.kind === 'edit' && view.id) {
      try {
        await freezeSourcesUsesScenarioApi({ tenant: TENANT, scenarioId: view.id });
      } catch {
        error('The scenario could not be finished. Finish it from the list.');
      }
    }
    setDirty(false);
    setOpenScenario(null);
    setView(LIST);
  };

  const requestLeave = () => {
    if (dirty || view.kind === 'edit') {
      setLeaving(true);
      return;
    }
    leave();
  };

  const saveAndLeave = async () => {
    setSavingToLeave(true);
    try {
      const saved = await panelRef.current?.saveAll();
      if (saved) await leave();
    } finally {
      setSavingToLeave(false);
    }
  };

  if (view.kind === 'list') {
    return (
      <Box maxW="container.xl" mt={8}>
        <Text fontSize="2xl" fontWeight={700} mb={4}>Sources &amp; Uses</Text>
        <SourcesUsesScenarioList
          scenarios={scenarios}
          loading={listLoading}
          busyId={busyId}
          onOpenMain={openMain}
          onNew={startNew}
          onOpen={openFromList}
          onFinish={finish}
          onDelete={remove}
        />
      </Box>
    );
  }

  const tabs = TABS;
  const readOnly = view.kind === 'view';
  const panelMode = view.kind;

  return (
    <Box maxW="container.xl">
      <Flex align="center" gap={3} wrap="wrap" mb={3} mt={4}>
        <Button size="sm" variant="ghost" leftIcon={<ArrowBackIcon />} onClick={requestLeave}>
          All scenarios
        </Button>
        {view.kind === 'main' && (
          <>
            <Text fontWeight={700}>Main</Text>
            <Badge colorScheme="gray">Current data, not a scenario</Badge>
          </>
        )}
        {view.kind === 'edit' && (
          <>
            <Text fontWeight={700}>{`Scenario: ${view.name}`}</Text>
            <Badge colorScheme="orange">{view.id ? 'Not finished' : 'Not saved yet'}</Badge>
            <Text fontSize="sm" color="gray.500">
              {view.id
                ? 'Each Save & Run updates Main and this scenario. Leaving finishes it.'
                : 'Starts from Main. Press Save & Run to save it.'}
            </Text>
          </>
        )}
        {readOnly && (
          <>
            <Text fontWeight={700}>{`Scenario: ${view.name}`}</Text>
            <Badge colorScheme="green">Finished, read-only</Badge>
            {openScenario && <Text fontSize="sm" color="gray.500">{scenarioWindowText(openScenario)}</Text>}
          </>
        )}
      </Flex>
      <Tabs
        index={Math.max(0, SCREENS.indexOf(screen))}
        onChange={(index) => setScreen(SCREENS[index])}
        colorScheme="brand"
        mb={4}
      >
        <TabList>
          {tabs.map((tab) => <Tab key={tab.screen}>{tab.label}</Tab>)}
        </TabList>
      </Tabs>
      {readOnly && !openScenario ? (
        <Flex justify="center" py={16}><Spinner /></Flex>
      ) : (
        <CashFlowFeeExposurePanel
          // A different scenario or mode starts a fresh panel.
          key={`${view.kind}:${view.kind === 'edit' ? 'scenario' : view.id || ''}`}
          ref={panelRef}
          tenantFilter={TENANT}
          screen={screen}
          mode={panelMode}
          scenario={readOnly ? openScenario : null}
          scenarioId={view.kind === 'edit' ? view.id : ''}
          scenarioName={view.name || ''}
          onScenarioSaved={(saved) => {
            if (saved?.id) setView((current) => ({ ...current, id: saved.id, name: saved.name || current.name }));
          }}
          onDirtyChange={setDirty}
        />
      )}

      <Modal isOpen={leaving} onClose={() => setLeaving(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {dirty ? 'You have unsaved changes' : 'Leave this scenario?'}
          </ModalHeader>
          <ModalBody>
            <Text fontSize="sm" mb={2}>
              {dirty
                ? 'Are you sure you want to leave? Changes that are not saved will be lost.'
                : 'Everything is saved.'}
            </Text>
            {view.kind === 'edit' && (
              <Text fontSize="sm" color="gray.600">
                {view.id
                  ? 'Leaving finishes this scenario. After that it can only be viewed.'
                  : 'This scenario is not saved yet. Save and leave runs Save & Run first.'}
              </Text>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setLeaving(false)}>Cancel</Button>
            <Button size="sm" variant="outline" onClick={leave}>
              {dirty ? 'Leave without saving' : 'Leave'}
            </Button>
            {dirty && (
              <Button size="sm" colorScheme="brand" isLoading={savingToLeave} onClick={saveAndLeave}>
                Save and leave
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SourcesAndUses;
